import {
  DeleteOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  FilterOutlined,
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined,
  SyncOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import {
  Button,
  Card,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Select,
  Space,
  Switch,
  Tag,
  Tooltip,
  message,
} from 'antd';
import dayjs from 'dayjs';
import React, {useMemo, useState} from 'react';
import {GiMedicines} from 'react-icons/gi';
import {useSelector} from 'react-redux';

import StatCard from '../../../CommonComponents/Cards/StateCard';
import TableData from '../../../CommonComponents/TableData/TableData';
import {
  RQMedicineAddAPI,
  RQMedicineDeleteAPI,
  RQMedicineLookupAllAPI,
  RQMedicineUpdateAPI,
} from '../../../ReactQuery/apis';
import {selectAuth} from '../../../Redux/selectors';

const {Option} = Select;

const FILTERS = {
  ALL: 'all',
  LOW_STOCK: 'low-stock',
  OUT_OF_STOCK: 'out-of-stock',
  EXPIRING: 'expiring-soon',
  EXPIRED: 'expired',
};

const MEDICINE_TYPES = {
  ALL: 'all',
  INVENTORY: 'inventory',
  NON_INVENTORY: 'non-inventory',
};

const BACKEND_MEDICINE_TYPES = {
  INVENTORY: 'INVENTORY',
  NON_INVENTORY: 'NON_INVENTORY',
};

const FILTER_OPTIONS = [
  {label: 'All Medicines', value: FILTERS.ALL, icon: null},
  {
    label: 'Low Stock',
    value: FILTERS.LOW_STOCK,
    icon: <WarningOutlined style={{color: '#faad14'}} />,
  },
  {
    label: 'Out of Stock',
    value: FILTERS.OUT_OF_STOCK,
    icon: <ExclamationCircleOutlined style={{color: '#f5222d'}} />,
  },
  {
    label: 'Expiring Soon',
    value: FILTERS.EXPIRING,
    icon: <WarningOutlined style={{color: '#fa8c16'}} />,
  },
  {
    label: 'Expired',
    value: FILTERS.EXPIRED,
    icon: <ExclamationCircleOutlined style={{color: '#cf1322'}} />,
  },
];

const SEARCH_TYPES = {
  NAME: 'name',
  GENERIC: 'generic',
  MANUFACTURER: 'manufacturer',
};

const EXPIRY_WARNING_DAYS = 30;

const normalizeMedicineType = value => {
  const type = String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/[_\s]+/g, '-');

  if (
    [
      'non-inventory',
      'noninventory',
      'non-inventory-medicine',
      'non-inventory-medicines',
      'external',
      'external-medicine',
    ].includes(type)
  ) {
    return MEDICINE_TYPES.NON_INVENTORY;
  }

  return MEDICINE_TYPES.INVENTORY;
};

const getBackendMedicineType = value =>
  normalizeMedicineType(value) === MEDICINE_TYPES.NON_INVENTORY
    ? BACKEND_MEDICINE_TYPES.NON_INVENTORY
    : BACKEND_MEDICINE_TYPES.INVENTORY;

const removeUndefinedValues = object =>
  Object.fromEntries(
    Object.entries(object).filter(([, value]) => value !== undefined),
  );

const MedicineManagementScreen = () => {
  const auth = useSelector(selectAuth);
  const token = auth?.tokenData;

  const [activeFilter, setActiveFilter] = useState(FILTERS.ALL);
  const [medicineType, setMedicineType] = useState(MEDICINE_TYPES.ALL);
  const [searchType, setSearchType] = useState(SEARCH_TYPES.NAME);
  const [searchTerm, setSearchTerm] = useState('');
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [editingMedicine, setEditingMedicine] = useState(null);
  const [pendingRowId, setPendingRowId] = useState(null);

  const [form] = Form.useForm();
  const [messageApi, messageContextHolder] = message.useMessage();

  const allMedicinesQuery = RQMedicineLookupAllAPI(token);
  const allMedicines = allMedicinesQuery.data ?? [];
  const loading = allMedicinesQuery.isFetching;

  const getMedicineType = medicine =>
    normalizeMedicineType(
      medicine?.medicineType ??
        medicine?.type ??
        medicine?.medicine_type ??
        medicine?.medicineCategory,
    );

  const enrichedMedicines = useMemo(() => {
    const now = dayjs();

    return allMedicines.map(medicine => {
      const availableStock = Number(medicine?.availableStock ?? 0);
      const minimumStock = Number(medicine?.minimumStock ?? 0);
      const expiryDate = medicine?.expiryDate
        ? dayjs(medicine.expiryDate)
        : null;

      const outOfStock = availableStock <= 0;
      const lowStock = !outOfStock && availableStock <= minimumStock;
      const expired = Boolean(expiryDate) && expiryDate.isBefore(now, 'day');
      const expiringSoon =
        Boolean(expiryDate) &&
        !expired &&
        expiryDate.diff(now, 'day') <= EXPIRY_WARNING_DAYS;

      return {
        ...medicine,
        medicineType: getMedicineType(medicine),
        availableStock,
        minimumStock,
        outOfStock,
        lowStock,
        expired,
        expiringSoon,
      };
    });
  }, [allMedicines]);

  const stats = useMemo(
    () => ({
      lowStock: enrichedMedicines.filter(medicine => medicine.lowStock).length,
      outOfStock: enrichedMedicines.filter(medicine => medicine.outOfStock)
        .length,
      expiringSoon: enrichedMedicines.filter(medicine => medicine.expiringSoon)
        .length,
      expired: enrichedMedicines.filter(medicine => medicine.expired).length,
    }),
    [enrichedMedicines],
  );

  const isSearching = Boolean(searchTerm);

  const medicines = useMemo(() => {
    let list = enrichedMedicines;

    if (medicineType !== MEDICINE_TYPES.ALL) {
      list = list.filter(
        medicine => medicine.medicineType === medicineType,
      );
    }

    if (isSearching) {
      const term = searchTerm.toLowerCase();

      const fieldMap = {
        [SEARCH_TYPES.NAME]: 'medicineName',
        [SEARCH_TYPES.GENERIC]: 'genericName',
        [SEARCH_TYPES.MANUFACTURER]: 'manufacturer',
      };

      const field = fieldMap[searchType];

      return list.filter(medicine =>
        String(medicine?.[field] || '')
          .toLowerCase()
          .includes(term),
      );
    }

    switch (activeFilter) {
      case FILTERS.LOW_STOCK:
        return list.filter(medicine => medicine.lowStock);
      case FILTERS.OUT_OF_STOCK:
        return list.filter(medicine => medicine.outOfStock);
      case FILTERS.EXPIRING:
        return list.filter(medicine => medicine.expiringSoon);
      case FILTERS.EXPIRED:
        return list.filter(medicine => medicine.expired);
      default:
        return list;
    }
  }, [
    enrichedMedicines,
    medicineType,
    isSearching,
    searchTerm,
    searchType,
    activeFilter,
  ]);

  const closeFormModal = () => {
    setIsFormModalOpen(false);
    setEditingMedicine(null);
    form.resetFields();
  };

  const addMedicine = RQMedicineAddAPI({
    onSuccess: () => {
      messageApi.success('Medicine added successfully');
      allMedicinesQuery.refetch();
      closeFormModal();
    },
    onError: error => {
      console.error('Medicine add API error:', error);
      console.error('Status:', error?.response?.status);
      console.error('Backend response:', error?.response?.data);
      console.error('Request payload:', error?.config?.data);

      const backendMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.response?.data?.detail ||
        error?.response?.data?.errors ||
        error?.message ||
        'Failed to add medicine';

      messageApi.error(String(backendMessage));
    },
  });

  const updateMedicine = RQMedicineUpdateAPI({
    onSuccess: () => {
      messageApi.success('Medicine updated successfully');
      allMedicinesQuery.refetch();
      closeFormModal();
    },
    onError: error => {
      console.error('Medicine update API error:', error);
      console.error('Status:', error?.response?.status);
      console.error('Backend response:', error?.response?.data);
      console.error('Request payload:', error?.config?.data);

      const backendMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.response?.data?.detail ||
        error?.response?.data?.errors ||
        error?.message ||
        'Failed to update medicine';

      messageApi.error(String(backendMessage));
    },
    onSettled: () => {
      setPendingRowId(null);
    },
  });

  const deleteMedicine = RQMedicineDeleteAPI({
    onSuccess: () => {
      messageApi.success('Medicine deleted successfully');
      allMedicinesQuery.refetch();
    },
    onError: error => {
      console.error('Medicine delete API error:', error);
      console.error('Backend response:', error?.response?.data);

      const backendMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.response?.data?.detail ||
        error?.message ||
        'Failed to delete medicine';

      messageApi.error(String(backendMessage));
    },
    onSettled: () => {
      setPendingRowId(null);
    },
  });

  const handleFilterChange = filter => {
    setSearchTerm('');
    setActiveFilter(filter);
  };

  const handleMedicineTypeChange = type => {
    setMedicineType(
      type === MEDICINE_TYPES.ALL
        ? MEDICINE_TYPES.ALL
        : normalizeMedicineType(type),
    );
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setActiveFilter(FILTERS.ALL);
    setMedicineType(MEDICINE_TYPES.ALL);
  };

  const hasActiveFilter =
    isSearching ||
    activeFilter !== FILTERS.ALL ||
    medicineType !== MEDICINE_TYPES.ALL;

  const openAddModal = () => {
    setEditingMedicine(null);
    form.resetFields();

    form.setFieldsValue({
      medicineType: MEDICINE_TYPES.INVENTORY,
      active: true,
    });

    setIsFormModalOpen(true);
  };

  const openEditModal = record => {
    if (pendingRowId === record?.medicineId) {
      return;
    }

    setEditingMedicine(record);
    form.resetFields();

    form.setFieldsValue({
      medicineName: record?.medicineName || '',
      genericName: record?.genericName || '',
      brandName: record?.brandName || '',
      manufacturer: record?.manufacturer || '',
      medicineType: getMedicineType(record),
      dosageForm: record?.dosageForm || '',
      batchNumber: record?.batchNumber || '',
      strength: record?.strength ?? undefined,
      strengthUnit: record?.strengthUnit || '',
      packType: record?.packType || '',
      packSize: record?.packSize ?? undefined,
      purchasePrice: record?.purchasePrice ?? undefined,
      sellingPrice: record?.sellingPrice ?? undefined,
      minimumStock: record?.minimumStock ?? undefined,
      availableStock: record?.availableStock ?? undefined,
      expiryDate: record?.expiryDate ? dayjs(record.expiryDate) : null,
      active: record?.active ?? true,
      storageCondition: record?.storageCondition || '',
      description: record?.description || '',
    });

    setIsFormModalOpen(true);
  };

  const handleFormSubmit = async () => {
    try {
      const values = await form.validateFields();

      const backendMedicineType = getBackendMedicineType(
        values.medicineType,
      );

      const payload = removeUndefinedValues({
        medicineName: values.medicineName?.trim(),
        genericName: values.genericName?.trim(),
        brandName: values.brandName?.trim(),
        strength: values.strength,
        strengthUnit: values.strengthUnit?.trim(),
        dosageForm: values.dosageForm?.trim(),
        packType: values.packType?.trim(),
        packSize: values.packSize,
        batchNumber: values.batchNumber?.trim(),
        expiryDate: values.expiryDate
          ? values.expiryDate.format('YYYY-MM-DD')
          : undefined,
        purchasePrice: values.purchasePrice,
        sellingPrice: values.sellingPrice,
        availableStock: values.availableStock,
        minimumStock: values.minimumStock,
        manufacturer: values.manufacturer?.trim(),
        description: values.description?.trim(),
        storageCondition: values.storageCondition?.trim(),
        active: values.active,
        medicineType: backendMedicineType,
      });

      console.log(
        editingMedicine ? 'MEDICINE UPDATE PAYLOAD' : 'MEDICINE ADD PAYLOAD',
      );
      console.log('FINAL PAYLOAD:', payload);
      console.log(
        'FINAL PAYLOAD JSON:',
        JSON.stringify(payload, null, 2),
      );

      if (editingMedicine) {
        setPendingRowId(editingMedicine.medicineId);

        updateMedicine.mutate({
          id: editingMedicine.medicineId,
          formData: payload,
          token,
        });

        return;
      }

      if (!token) {
        messageApi.error(
          'You are not logged in. Please log in again.',
        );
        return;
      }

      addMedicine.mutate({
        formData: payload,
        token,
      });
    } catch (errorInfo) {
      console.warn('Medicine form validation failed:', errorInfo);

      if (errorInfo?.errorFields) {
        messageApi.error(
          'Please fill all required fields correctly.',
        );
      }
    }
  };

  const handleDelete = id => {
    if (!id) {
      return;
    }

    setPendingRowId(id);

    deleteMedicine.mutate({
      id,
      token,
    });
  };

  const columns = [
    {
      title: 'Sr. No.',
      key: 'srno',
      width: 70,
      render: (_, __, index) => index + 1,
    },
    {
      title: 'Medicine Name',
      dataIndex: 'medicineName',
      sorter: (a, b) =>
        String(a?.medicineName || '').localeCompare(
          String(b?.medicineName || ''),
        ),
      render: (name, record) => (
        <button
          type={'button'}
          onClick={() => openEditModal(record)}
          className={
            'cursor-pointer border-0 bg-transparent p-0 text-left text-base font-semibold hover:text-primary-800 hover:underline'
          }
        >
          {name}
        </button>
      ),
    },
    {
      title: 'Generic Name',
      dataIndex: 'genericName',
    },
    {
      title: 'Brand Name',
      dataIndex: 'brandName',
    },
    {
      title: 'Manufacturer',
      dataIndex: 'manufacturer',
    },
    {
      title: 'Medicine Type',
      dataIndex: 'medicineType',
      render: value =>
        normalizeMedicineType(value) === MEDICINE_TYPES.NON_INVENTORY ? (
          <Tag color={'orange'}>Non Inventory</Tag>
        ) : (
          <Tag color={'blue'}>Inventory</Tag>
        ),
    },
    {
      title: 'Strength',
      key: 'strength',
      render: (_, record) =>
        record?.strength != null
          ? `${record.strength}${record?.strengthUnit || ''}`
          : '-',
    },
    {
      title: 'Dosage Form',
      dataIndex: 'dosageForm',
    },
    {
      title: 'Pack',
      key: 'pack',
      render: (_, record) =>
        record?.packType
          ? `${record.packType}${
            record?.packSize ? ` (${record.packSize})` : ''
          }`
          : '-',
    },
    {
      title: 'Batch No.',
      dataIndex: 'batchNumber',
    },
    {
      title: 'Purchase Price',
      dataIndex: 'purchasePrice',
      render: value => `₹${value ?? 0}`,
    },
    {
      title: 'Selling Price',
      dataIndex: 'sellingPrice',
      render: value => `₹${value ?? 0}`,
    },
    {
      title: 'Stock',
      dataIndex: 'availableStock',
      sorter: (a, b) =>
        (a?.availableStock ?? 0) - (b?.availableStock ?? 0),
    },
    {
      title: 'Min Stock',
      dataIndex: 'minimumStock',
    },
    {
      title: 'Expiry Date',
      dataIndex: 'expiryDate',
      sorter: (a, b) =>
        new Date(a?.expiryDate ?? 0) -
        new Date(b?.expiryDate ?? 0),
      render: value =>
        value ? dayjs(value).format('DD-MM-YYYY') : '-',
    },
    {
      title: 'Active',
      dataIndex: 'active',
      render: value =>
        value ? (
          <Tag color={'blue'}>Active</Tag>
        ) : (
          <Tag>Inactive</Tag>
        ),
    },
    {
      title: 'Status',
      key: 'status',
      render: (_, record) => (
        <Space orientation={'vertical'} size={2}>
          {record?.outOfStock && (
            <Tag color={'red'} className={'font-medium'}>
              Out of Stock
            </Tag>
          )}

          {!record?.outOfStock && record?.lowStock && (
            <Tag color={'orange'} className={'font-medium'}>
              Low Stock
            </Tag>
          )}

          {record?.expired && (
            <Tag color={'volcano'} className={'font-medium'}>
              Expired
            </Tag>
          )}

          {!record?.expired && record?.expiringSoon && (
            <Tag color={'gold'} className={'font-medium'}>
              Expiring Soon
            </Tag>
          )}

          {!record?.outOfStock &&
            !record?.lowStock &&
            !record?.expired &&
            !record?.expiringSoon && (
            <Tag color={'green'} className={'font-medium'}>
                Healthy
            </Tag>
          )}
        </Space>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      fixed: 'right',
      render: (_, record) => {
        const rowBusy = pendingRowId === record?.medicineId;
        const isDeletingThisRow =
          rowBusy && deleteMedicine.isPending;

        return (
          <Space size={4}>
            <Tooltip title={'Edit'}>
              <Button
                size={'small'}
                icon={<EditOutlined />}
                onClick={() => openEditModal(record)}
                disabled={rowBusy}
                className={
                  'border-primary-200 text-primary-600 hover:border-primary-400'
                }
              />
            </Tooltip>

            <Tooltip title={'Delete'}>
              <Popconfirm
                title={'Delete this medicine?'}
                description={'This action cannot be undone.'}
                onConfirm={() =>
                  handleDelete(record?.medicineId)
                }
                okButtonProps={{
                  danger: true,
                  loading: isDeletingThisRow,
                }}
                okText={'Delete'}
                disabled={rowBusy}
              >
                <Button
                  size={'small'}
                  icon={<DeleteOutlined />}
                  danger
                  loading={isDeletingThisRow}
                  disabled={rowBusy && !isDeletingThisRow}
                />
              </Popconfirm>
            </Tooltip>
          </Space>
        );
      },
    },
  ];

  return (
    <>
      {messageContextHolder}

      <div className={'min-h-screen p-2 md:p-6'}>
        <div
          className={
            'mb-6 flex flex-wrap items-center justify-between gap-3'
          }
        >
          <div className={'flex items-center gap-3'}>
            <div
              className={
                'flex h-10 w-12 items-center justify-center rounded-xl text-white'
              }
              style={{
                background: 'linear-gradient(135deg, #b69c66, #b69c66)',
              }}
            >
              <GiMedicines style={{fontSize: 22}} />
            </div>

            <h1 className={'title !mb-0'}>Medicine Management</h1>
          </div>

          <Space>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => allMedicinesQuery.refetch()}
              loading={loading}
              className={'btn-secondary'}
            >
              Refresh
            </Button>

            <Button
              type={'primary'}
              icon={<PlusOutlined />}
              onClick={openAddModal}
              className={'btn-primary'}
            >
              Add Medicine
            </Button>
          </Space>
        </div>

        <div
          className={
            'mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4'
          }
        >
          <button
            type={'button'}
            onClick={() => handleFilterChange(FILTERS.LOW_STOCK)}
            className={'cursor-pointer border-0 bg-transparent p-0 text-left'}
          >
            <StatCard
              icon={<WarningOutlined />}
              label={'Low Stock'}
              value={stats.lowStock}
              loading={loading}
              colorClass={
                activeFilter === FILTERS.LOW_STOCK
                  ? 'bg-primary-50'
                  : 'bg-white'
              }
            />
          </button>

          <button
            type={'button'}
            onClick={() => handleFilterChange(FILTERS.OUT_OF_STOCK)}
            className={'cursor-pointer border-0 bg-transparent p-0 text-left'}
          >
            <StatCard
              icon={<ExclamationCircleOutlined />}
              label={'Out of Stock'}
              value={stats.outOfStock}
              loading={loading}
              colorClass={
                activeFilter === FILTERS.OUT_OF_STOCK
                  ? 'bg-primary-50'
                  : 'bg-white'
              }
            />
          </button>

          <button
            type={'button'}
            onClick={() => handleFilterChange(FILTERS.EXPIRING)}
            className={'cursor-pointer border-0 bg-transparent p-0 text-left'}
          >
            <StatCard
              icon={<WarningOutlined />}
              label={'Expiring Soon'}
              value={stats.expiringSoon}
              loading={loading}
              colorClass={
                activeFilter === FILTERS.EXPIRING
                  ? 'bg-primary-50'
                  : 'bg-white'
              }
            />
          </button>

          <button
            type={'button'}
            onClick={() => handleFilterChange(FILTERS.EXPIRED)}
            className={'cursor-pointer border-0 bg-transparent p-0 text-left'}
          >
            <StatCard
              icon={<ExclamationCircleOutlined />}
              label={'Expired'}
              value={stats.expired}
              loading={loading}
              colorClass={
                activeFilter === FILTERS.EXPIRED
                  ? 'bg-primary-50'
                  : 'bg-white'
              }
            />
          </button>
        </div>

        <div className={'mb-5'}>
          <div className={'mb-3 flex items-center gap-2'}>
            <FilterOutlined className={'text-primary-500'} />

            <span className={'text-base font-semibold text-gray-700'}>
              Filters
            </span>

            {hasActiveFilter && (
              <Tag color={'blue'} className={'ml-1 text-xs'}>
                {medicines.length} result
                {medicines.length !== 1 ? 's' : ''}
              </Tag>
            )}
          </div>

          <div className={'flex flex-wrap items-center gap-3'}>
            <Select
              value={isSearching ? FILTERS.ALL : activeFilter}
              onChange={handleFilterChange}
              style={{width: 200}}
              placeholder={'Filter by status'}
            >
              {FILTER_OPTIONS.map(option => (
                <Option key={option.value} value={option.value}>
                  <Space size={6}>
                    {option.icon}
                    {option.label}
                  </Space>
                </Option>
              ))}
            </Select>

            <Select
              value={medicineType}
              onChange={handleMedicineTypeChange}
              style={{width: 180}}
              placeholder={'Filter by medicine type'}
            >
              <Option value={MEDICINE_TYPES.ALL}>All Medicines</Option>
              <Option value={MEDICINE_TYPES.INVENTORY}>Inventory</Option>
              <Option value={MEDICINE_TYPES.NON_INVENTORY}>
                Non Inventory
              </Option>
            </Select>

            <Select
              value={searchType}
              onChange={setSearchType}
              style={{width: 150}}
            >
              <Option value={SEARCH_TYPES.NAME}>Name</Option>
              <Option value={SEARCH_TYPES.GENERIC}>Generic Name</Option>
              <Option value={SEARCH_TYPES.MANUFACTURER}>
                Manufacturer
              </Option>
            </Select>

            <Input
              prefix={<SearchOutlined className={'text-gray-400'} />}
              placeholder={`Search by ${searchType}…`}
              value={searchTerm}
              onChange={event =>
                setSearchTerm(event.target.value.trim())
              }
              allowClear
              style={{width: 260}}
            />

            {hasActiveFilter && (
              <Button
                icon={<SyncOutlined />}
                onClick={handleClearFilters}
              >
                Reset
              </Button>
            )}
          </div>
        </div>

        <Card
          className={'rounded-lg border border-primary-100 shadow-sm'}
          styles={{body: {padding: 16}}}
        >
          <TableData
            dataSource={medicines}
            columns={columns}
            rowKey={'medicineId'}
            loading={loading}
            size={'middle'}
            scroll={{x: 1900}}
            rowClassName={'transition-colors hover:bg-primary-50'}
          />
        </Card>

        <Modal
          title={(
            <span className={'text-base font-bold text-primary-700'}>
              {editingMedicine ? 'Edit Medicine' : 'Add Medicine'}
            </span>
          )}
          open={isFormModalOpen}
          onCancel={closeFormModal}
          onOk={handleFormSubmit}
          confirmLoading={
            addMedicine.isPending || updateMedicine.isPending
          }
          width={760}
          okText={editingMedicine ? 'Update' : 'Add'}
          okButtonProps={{
            className: 'custom-modal-btn',
          }}
          forceRender
        >
          <Form
            form={form}
            layout={'vertical'}
            className={'mt-4'}
          >
            <div className={'grid grid-cols-2 gap-x-4'}>
              <Form.Item
                name={'medicineName'}
                label={'Medicine Name'}
                rules={[
                  {
                    required: false,
                    message: 'Medicine name is required',
                  },
                ]}
              >
                <Input />
              </Form.Item>

              <Form.Item name={'genericName'} label={'Generic Name'}>
                <Input />
              </Form.Item>

              <Form.Item name={'brandName'} label={'Brand Name'}>
                <Input />
              </Form.Item>

              <Form.Item name={'manufacturer'} label={'Manufacturer'}>
                <Input />
              </Form.Item>

              <Form.Item
                name={'medicineType'}
                label={'Medicine Type'}
              >
                <Select placeholder={'Select medicine type'}>
                  <Option value={MEDICINE_TYPES.INVENTORY}>
                    Inventory
                  </Option>
                  <Option value={MEDICINE_TYPES.NON_INVENTORY}>
                    Non Inventory
                  </Option>
                </Select>
              </Form.Item>

              <Form.Item
                name={'dosageForm'}
                label={'Dosage Form'}
                // rules={[
                //   {
                //     required: true,
                //     message: 'Dosage form is required',
                //   },
                // ]}
              >
                <Input
                  placeholder={'e.g. Capsule, Tablet, Syrup'}
                />
              </Form.Item>

              <Form.Item
                name={'batchNumber'}
                label={'Batch Number'}
              >
                <Input />
              </Form.Item>
            </div>

            <div className={'grid grid-cols-4 gap-x-4'}>
              <Form.Item name={'strength'} label={'Strength'}>
                <InputNumber
                  min={0}
                  className={'w-full'}
                />
              </Form.Item>

              <Form.Item
                name={'strengthUnit'}
                label={'Strength Unit'}
              >
                <Input placeholder={'e.g. mg'} />
              </Form.Item>

              <Form.Item name={'packType'} label={'Pack Type'}>
                <Input placeholder={'e.g. Box'} />
              </Form.Item>

              <Form.Item name={'packSize'} label={'Pack Size'}>
                <InputNumber
                  min={0}
                  className={'w-full'}
                />
              </Form.Item>
            </div>

            <div className={'grid grid-cols-4 gap-x-4'}>
              <Form.Item
                name={'purchasePrice'}
                label={'Purchase Price'}
                // rules={[
                //   {
                //     required: true,
                //     message: 'Required',
                //   },
                // ]}
              >
                <InputNumber
                  min={0}
                  className={'w-full'}
                />
              </Form.Item>

              <Form.Item
                name={'sellingPrice'}
                label={'Selling Price'}
                // rules={[
                //   {
                //     required: true,
                //     message: 'Required',
                //   },
                // ]}
              >
                <InputNumber
                  min={0}
                  className={'w-full'}
                />
              </Form.Item>

              <Form.Item
                name={'minimumStock'}
                label={'Minimum Stock'}
                // rules={[
                //   {
                //     required: true,
                //     message: 'Required',
                //   },
                // ]}
              >
                <InputNumber
                  min={0}
                  className={'w-full'}
                />
              </Form.Item>

              <Form.Item
                name={'availableStock'}
                label={
                  editingMedicine
                    ? 'Available Stock'
                    : 'Initial Stock'
                }
                // rules={[
                //   {
                //     required: true,
                //     message: 'Required',
                //   },
                // ]}
              >
                <InputNumber
                  min={0}
                  className={'w-full'}
                />
              </Form.Item>
            </div>

            <div className={'grid grid-cols-2 items-center gap-x-4'}>
              <Form.Item
                name={'expiryDate'}
                label={'Expiry Date'}
              >
                <DatePicker className={'w-full'} />
              </Form.Item>

              <Form.Item
                name={'active'}
                label={'Active'}
                valuePropName={'checked'}
              >
                <Switch />
              </Form.Item>
            </div>

            <Form.Item
              name={'storageCondition'}
              label={'Storage Condition'}
            >
              <Input
                placeholder={'e.g. Cool, Dry Place'}
              />
            </Form.Item>

            <Form.Item
              name={'description'}
              label={'Description'}
            >
              <Input.TextArea rows={2} />
            </Form.Item>
          </Form>
        </Modal>
      </div>
    </>
  );
};

export default MedicineManagementScreen;
